/**
 * <mega-menu-rotator> - auto-changes product images inside a mega-menu dropdown.
 * - Always shows an image; rotates on its own whenever the dropdown is open and nothing is hovered.
 * - Hovering a menu link shows that category/product image; leaving the link resumes rotation.
 * - A slide is only shown once its image has loaded, and broken images are skipped.
 * - Each slide links to its product page.
 */
class MegaMenuRotator extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.mm-rotator__slide'));
    this.tabs = Array.from(this.querySelectorAll('.mm-rotator__tab'));
    if (!this.slides.length) return;

    this.abort?.abort();
    this.abort = new AbortController();
    const opts = { signal: this.abort.signal };
    this.delay = Math.max(2, parseFloat(this.dataset.interval) || 4) * 1000;
    this.index = -1;
    this.pending = -1;
    this.paused = new Set();
    this.lastSwitch = 0;

    this.slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) {
        slide.dataset.broken = 'true';
        return;
      }
      img.loading = 'eager';
      img.addEventListener('error', () => this.markBroken(slide), { ...opts, once: true });
    });

    this.setActive(this.firstUsable());

    this.tabs.forEach((tab) =>
      tab.addEventListener('click', () => this.goTo(tab.dataset.cat, null, true), opts)
    );

    // Pause only while the pointer is over the image panel itself
    this.addEventListener('pointerenter', () => this.paused.add('self'), opts);
    this.addEventListener('pointerleave', () => this.resumeFrom('self'), opts);

    // Hovering a menu link shows its image, leaving it resumes auto-rotation
    const scope = this.closest('.mega-menu__grid') || this.parentElement;
    scope?.querySelectorAll('[data-rotator-cat]').forEach((link) => {
      const enter = () => {
        this.paused.add('link');
        this.goTo(link.dataset.rotatorCat, link.dataset.rotatorProduct);
      };
      const leave = () => this.resumeFrom('link');
      link.addEventListener('pointerenter', enter, opts);
      link.addEventListener('pointerleave', leave, opts);
      link.addEventListener('focus', enter, opts);
      link.addEventListener('blur', leave, opts);
    });

    // When the dropdown opens, make sure an image is showing and restart the clock
    this.submenu = this.closest('.menu-list__submenu');
    if (this.submenu) {
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => {
        if (this.isOpen()) {
          this.paused.clear();
          this.ensureActive();
          this.lastSwitch = Date.now();
        }
      });
      this.observer.observe(this.submenu, { attributes: true, attributeFilter: ['inert'] });
    }

    // One steady clock: advances while open and not hovered; also self-heals a missing active image
    clearInterval(this.clock);
    this.clock = setInterval(() => this.tick(), 250);
  }

  disconnectedCallback() {
    clearInterval(this.clock);
    this.abort?.abort();
    this.observer?.disconnect();
  }

  usable(slide) {
    return slide && slide.dataset.broken !== 'true';
  }

  firstUsable() {
    const i = this.slides.findIndex((s) => this.usable(s));
    return i < 0 ? 0 : i;
  }

  isOpen() {
    return !this.submenu || !this.submenu.inert;
  }

  resumeFrom(reason) {
    this.paused.delete(reason);
    this.lastSwitch = Date.now();
  }

  ensureActive() {
    const active = this.slides[this.index];
    if (!this.usable(active)) {
      this.setActive(this.firstUsable());
    } else if (!active.classList.contains('is-active')) {
      this.setActive(this.index);
    }
  }

  tick() {
    this.ensureActive();
    if (!this.isOpen() || this.paused.size) return;
    if (Date.now() - this.lastSwitch < this.delay) return;
    this.next();
  }

  next() {
    const n = this.slides.length;
    for (let step = 1; step <= n; step++) {
      const i = (this.index + step) % n;
      if (this.usable(this.slides[i])) {
        this.show(i);
        return;
      }
    }
  }

  markBroken(slide) {
    slide.dataset.broken = 'true';
    if (this.slides[this.index] === slide) this.next();
  }

  goTo(cat, product, force = false) {
    const catStr = cat == null ? null : String(cat);
    const ok = (s) => this.usable(s);
    let i = -1;
    if (product) {
      i = this.slides.findIndex((s) => ok(s) && s.dataset.product === product && s.dataset.cat === catStr);
      if (i < 0) i = this.slides.findIndex((s) => ok(s) && s.dataset.product === product);
    }
    if (i < 0 && catStr != null) {
      if (!force && this.slides[this.index]?.dataset.cat === catStr) return;
      i = this.slides.findIndex((s) => ok(s) && s.dataset.cat === catStr);
    }
    // No image for this link: keep the current image instead of showing an empty frame
    if (i >= 0) this.show(i);
    this.lastSwitch = Date.now();
  }

  /** Show slide i once its image is ready; until then the current image stays visible. */
  show(i) {
    const slide = this.slides[i];
    if (!this.usable(slide)) return;
    if (i === this.index) {
      this.ensureActive();
      return;
    }
    const img = slide.querySelector('img');
    this.pending = i;
    if (!img || (img.complete && img.naturalWidth > 0)) {
      this.setActive(i);
      return;
    }
    img.addEventListener(
      'load',
      () => {
        if (this.pending === i) this.setActive(i);
      },
      { once: true, signal: this.abort.signal }
    );
  }

  setActive(i) {
    this.index = i;
    this.pending = -1;
    this.lastSwitch = Date.now();
    this.slides.forEach((slide, k) => {
      const on = k === i;
      slide.classList.toggle('is-active', on);
      slide.setAttribute('aria-hidden', String(!on));
      slide.tabIndex = on ? 0 : -1;
    });
    const cat = this.slides[i]?.dataset.cat;
    this.tabs.forEach((tab) => {
      const on = tab.dataset.cat === cat;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-pressed', String(on));
    });
  }
}

if (!customElements.get('mega-menu-rotator')) {
  customElements.define('mega-menu-rotator', MegaMenuRotator);
}
